import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import {
  fetchCompanies,
  fetchStores,
  fetchVendors,
  fetchCustomers,
  fetchUsers,
  fetchProducts,
  fetchRoles,
} from "../Services/masterDataService";
import { encryptedPost } from "../utils/secureApi";
import { fetchBootMasterData } from "../Services/bootService";
import API from "../Services/BaseUrl";
import { gracefulForceLogout } from "../Services/tokenUtils";

/**
 * MasterDataContext — global cache for shared dropdown data.
 * Follows the CategoryCacheContext pattern: localStorage + TTL + cross-tab sync.
 *
 * Usage:
 *   const { companies, stores, vendors, customers, users, products, loading, invalidate } = useMasterData();
 *
 * Data is lazy-loaded on first access and cached per TTL.
 * Call invalidate('companies') after CRUD to refresh.
 */

const MasterDataContext = createContext(null);

// ==================== CONFIG ====================

const DATASETS = {
  companies:  { ttl: 60 * 60 * 1000, fetch: fetchCompanies,  key: "md_companies" },
  stores:     { ttl: 60 * 60 * 1000, fetch: fetchStores,     key: "md_stores" },
  vendors:    { ttl: 30 * 60 * 1000, fetch: fetchVendors,    key: "md_vendors" },
  customers:  { ttl: 30 * 60 * 1000, fetch: fetchCustomers,  key: "md_customers" },
  users:      { ttl: 60 * 60 * 1000, fetch: fetchUsers,      key: "md_users" },
  products:   { ttl: 15 * 60 * 1000, fetch: fetchProducts,   key: "md_products" },
  roles:      { ttl: 60 * 60 * 1000, fetch: fetchRoles,      key: "md_roles" },
};

// ==================== localStorage helpers ====================

function readCache(key, ttl) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached?.data && cached?.ts && Date.now() - cached.ts < ttl) {
      return cached.data;
    }
    return null;
  } catch { return null; }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* ignore */ }
}

function clearCacheKey(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

// ==================== Provider ====================

export function MasterDataProvider({ children }) {
  // State for each dataset
  const [data, setData] = useState(() => {
    const initial = {};
    for (const name of Object.keys(DATASETS)) {
      const cfg = DATASETS[name];
      initial[name] = readCache(cfg.key, cfg.ttl) || [];
    }
    return initial;
  });

  const [loading, setLoading] = useState(false);
  const fetchRefs = useRef({}); // dedup concurrent calls per dataset

  // Fetch a single dataset (deduplicates concurrent calls)
  const fetchDataset = useCallback(async (name) => {
    const cfg = DATASETS[name];
    if (!cfg) return [];

    // Check cache first
    const cached = readCache(cfg.key, cfg.ttl);
    if (cached && cached.length > 0) {
      setData((prev) => ({ ...prev, [name]: cached }));
      return cached;
    }

    // Dedup: if already fetching, return same promise
    if (fetchRefs.current[name]) return fetchRefs.current[name];

    const promise = (async () => {
      try {
        const res = await cfg.fetch();
        if (res?.errCode === 0 && Array.isArray(res.data)) {
          setData((prev) => ({ ...prev, [name]: res.data }));
          writeCache(cfg.key, res.data);
          return res.data;
        }
        return data[name] || [];
      } catch {
        return data[name] || [];
      } finally {
        delete fetchRefs.current[name];
      }
    })();

    fetchRefs.current[name] = promise;
    return promise;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch specific datasets (call on mount)
  const prefetch = useCallback(async (names) => {
    setLoading(true);
    try {
      await Promise.all(names.map((n) => fetchDataset(n)));
    } finally {
      setLoading(false);
    }
  }, [fetchDataset]);

  // Invalidate a dataset. Default behavior: clear cache + refetch immediately.
  //
  // Pass { lazy: true } to skip the refetch — state goes to [], the localStorage
  // entry is wiped, and the next consumer's mount-time `prefetch` picks up the
  // fresh data on demand. Useful after a write when the current page already
  // refreshes its own view (e.g., Products.jsx fetchProducts(currentPage, …))
  // and cross-module consumers aren't visible yet. Saves one round-trip per
  // write without sacrificing consistency for the visible view.
  const invalidate = useCallback(async (name, { lazy = false } = {}) => {
    const cfg = DATASETS[name];
    if (!cfg) return;
    clearCacheKey(cfg.key);
    delete fetchRefs.current[name];

    if (lazy) {
      // Reset state so consumers' `.length === 0` guards retrigger on demand.
      setData((prev) => ({ ...prev, [name]: [] }));
      return;
    }

    // Eager refetch (default)
    try {
      const res = await cfg.fetch();
      if (res?.errCode === 0 && Array.isArray(res.data)) {
        setData((prev) => ({ ...prev, [name]: res.data }));
        writeCache(cfg.key, res.data);
      }
    } catch { /* ignore */ }
  }, []);

  // Auto-prefetch common data on mount (companies + stores — most pages need these).
  // Uses the shared /boot/master-data endpoint so CategoryCacheContext reuses
  // the same round-trip. Falls back to individual /list calls if boot returns
  // null for a dataset (user lacks permission) or the boot call itself fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const boot = await fetchBootMasterData();
      if (cancelled) return;

      const missing = [];

      if (Array.isArray(boot?.companies)) {
        setData((prev) => ({ ...prev, companies: boot.companies }));
        writeCache(DATASETS.companies.key, boot.companies);
      } else {
        missing.push("companies");
      }

      if (Array.isArray(boot?.stores)) {
        setData((prev) => ({ ...prev, stores: boot.stores }));
        writeCache(DATASETS.stores.key, boot.stores);
      } else {
        missing.push("stores");
      }

      if (missing.length) prefetch(missing);
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-tab sync: listen for localStorage changes from other tabs
  useEffect(() => {
    const handleStorage = (e) => {
      for (const [name, cfg] of Object.entries(DATASETS)) {
        if (e.key === cfg.key) {
          const cached = readCache(cfg.key, cfg.ttl);
          if (cached) {
            setData((prev) => ({ ...prev, [name]: cached }));
          }
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // ==================== AUTO-REFRESH via version polling ====================
  // Polls /cache/versions every 30s to detect cross-user data changes.
  // When server version increments (PostgreSQL NOTIFY), invalidate stale caches.
  const versionsRef = useRef({});
  const POLL_INTERVAL = 30000; // 30 seconds

  useEffect(() => {
    const pollVersions = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return; // not logged in

        // Release B: passive session heartbeat rides this 30s poll (no dedicated loop).
        // A false result means the session was revoked/expired server-side → log out.
        try {
          const hb = await API.post("/users/heartbeat");
          if (hb?.data?.errCode === 0 && hb.data.data && hb.data.data.valid === false) {
            // Graceful: if the user has unsaved work, offer to stay and save first.
            // If they defer, this returns false and the next 30s poll re-checks.
            if (gracefulForceLogout("You were signed out — the session ended or you signed in on another device.")) {
              return;
            }
          }
        } catch { /* heartbeat is best-effort */ }

        const res = await encryptedPost("/cache/versions", {});
        if (res?.errCode === 0 && res.data) {
          const serverVersions = res.data;
          const prev = versionsRef.current;

          // Compare each dataset — invalidate if version changed
          for (const name of Object.keys(DATASETS)) {
            if (serverVersions[name] !== undefined && prev[name] !== undefined
                && serverVersions[name] !== prev[name]) {
              const cfg = DATASETS[name];
              clearCacheKey(cfg.key);
              // Refetch in background
              cfg.fetch().then((fetchRes) => {
                if (fetchRes?.errCode === 0 && Array.isArray(fetchRes.data)) {
                  setData((d) => ({ ...d, [name]: fetchRes.data }));
                  writeCache(cfg.key, fetchRes.data);
                }
              }).catch(() => {});
            }
          }

          versionsRef.current = serverVersions;
        }
      } catch { /* ignore — polling is best-effort */ }
    };

    // First poll fires at POLL_INTERVAL (30 s). The boot path already supplies
    // fresh data, so the earlier t=5 s "baseline" poll was a wasted round-trip:
    // prev[name] is always undefined on the first call, so no refetch happens
    // anyway. Skipping it saves one request on every page load.
    const interval = setInterval(pollVersions, POLL_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    companies: data.companies,
    stores: data.stores,
    vendors: data.vendors,
    customers: data.customers,
    users: data.users,
    products: data.products,
    roles: data.roles,
    loading,
    prefetch,       // prefetch(['vendors','customers']) — lazy load specific datasets
    invalidate,     // invalidate('companies') — clear + refetch after CRUD
    fetchDataset,   // fetch single dataset on demand
  };

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
}

/**
 * Hook to access master data. Components call this to get cached data.
 *
 * Example:
 *   const { companies, stores, prefetch } = useMasterData();
 *   useEffect(() => { prefetch(['vendors','customers']); }, []);
 */
export function useMasterData() {
  const ctx = useContext(MasterDataContext);
  if (!ctx) throw new Error("useMasterData must be used within MasterDataProvider");
  return ctx;
}
